import ErrorState from '@rio-cloud/rio-uikit/ErrorState';
import Spinner from '@rio-cloud/rio-uikit/Spinner';
import { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { isUserSessionExpired } from '../../../../configuration/login/loginSlice';
import { useAppSelector } from '../../../../configuration/setup/hooks';
import {
    getDebouncedServiceOrderSearch,
    getServiceOrderCategory,
    getServiceOrderModalFilters,
    getServiceOrderSearchMode,
    getServiceOrderSort,
    getServiceOrderStatusFilter,
} from '../../reducers/serviceOrdersSlice';
import { LoadMoreButton } from '../../sharedComponents/table/LoadMoreButton';
import { useSetUserTriggeredFetching } from '../../sharedComponents/userTriggeredFetchingHook';
import { ServiceOrderCardsSorting } from './table/ServiceOrderCardsSorting';
import { ServiceOrderTable } from './table/ServiceOrdersTable';
import { ServiceCategory } from '../shared/commonServiceOrder.types';
import { ServiceOrderTableToolbar } from '../shared/toolbar/ServiceOrderTableToolbar';
import { ServiceOrderStatusFilter } from './table/toolbar/ServiceOrderStatusFilter';
import { ServiceOrderExportModal } from './table/toolbar/ServiceOrderExportModal';
import { TableColumnSettingsState } from '../../sharedComponents/table/TableColumnSettingsState';
import { serviceOrderColumnDefinition, ServiceOrdersTableColumns } from './table/ServiceOrdersTableColumns';
import { useGetServiceOrdersQuery } from '../../api/serviceOrdersApi';
import { mapServiceOrderFiltersToQueryParams, mapServiceOrderSearchToQueryParams } from '../shared/filter.types';
import { ServiceOrderBulkUpdate } from './bulkUpdate/ServiceOrderBulkUpdate';
import { ServiceOrderSearch } from './table/toolbar/ServiceOrderSearch';

export const DEFAULT_SERVICE_ORDERS_LIMIT_SIZE = 100;
export const MAX_SERVICE_ORDERS_LIMIT_SIZE = 1000;

const useGetServiceOrders = (limit: number) => {
    const isSessionExpired = useAppSelector(isUserSessionExpired);
    const statusFilter = useAppSelector(getServiceOrderStatusFilter);
    const modalFilters = useAppSelector(getServiceOrderModalFilters);
    const searchMode = useAppSelector(getServiceOrderSearchMode);
    const debouncedSearchTerm = useAppSelector(getDebouncedServiceOrderSearch).trim();
    const sort = useAppSelector(getServiceOrderSort);
    const category = useAppSelector(getServiceOrderCategory);

    const query = useGetServiceOrdersQuery(
        {
            ...mapServiceOrderFiltersToQueryParams(modalFilters, statusFilter, category),
            ...mapServiceOrderSearchToQueryParams(searchMode, debouncedSearchTerm),
            sortBy: sort,
            limit,
        },
        {
            pollingInterval: isSessionExpired ? undefined : 60000,
        },
    );

    useSetUserTriggeredFetching({
        isFetching: query.isFetching,
        depsWhichTriggerUserFetching: [statusFilter, modalFilters, debouncedSearchTerm, sort, category],
    });

    return query;
};

export const ServiceOrders = () => {
    const category = useAppSelector(getServiceOrderCategory);
    const [limit, setLimit] = useState(DEFAULT_SERVICE_ORDERS_LIMIT_SIZE);
    const nextLimit = limit + DEFAULT_SERVICE_ORDERS_LIMIT_SIZE;
    const isArchivedView = category === ServiceCategory.ARCHIVE;

    const { data: serviceOrderPage, isLoading, isError } = useGetServiceOrders(limit);

    if (isLoading) {
        return (
            <div data-testid="spinner">
                <Spinner />
            </div>
        );
    }

    if (isError || typeof serviceOrderPage === 'undefined') {
        return (
            <ErrorState
                headline={<FormattedMessage id="intl-msg:common-message.error.generic.headline" />}
                message={<FormattedMessage id="intl-msg:common-message.error.generic.message" />}
            />
        );
    }

    const serviceOrders = serviceOrderPage.items;

    const columnSettingTableId = isArchivedView ? 'serviceOrder.archived' : 'serviceOrder.order';
    const columnDefinition = getColumnDefinition(isArchivedView);
    return (
        <TableColumnSettingsState
            key={columnSettingTableId}
            tableId={columnSettingTableId}
            columnDefinition={columnDefinition}
        >
            <ServiceOrderTableToolbar
                statusFilter={<ServiceOrderStatusFilter />}
                search={<ServiceOrderSearch />}
                bulkUpdate={!isArchivedView ? <ServiceOrderBulkUpdate /> : undefined}
                exportModal={<ServiceOrderExportModal />}
            />
            <ServiceOrderCardsSorting />
            <ServiceOrderTable serviceOrders={serviceOrders} />
            {serviceOrders.length > 0 && (
                <LoadMoreButton
                    onLoadMore={() => setLimit(nextLimit)}
                    hasMoreToLoad={serviceOrderPage.hasMore}
                    limitReached={nextLimit > MAX_SERVICE_ORDERS_LIMIT_SIZE}
                />
            )}
        </TableColumnSettingsState>
    );
};

const getColumnDefinition = (isArchivedView: boolean) =>
    serviceOrderColumnDefinition.filter(
        (def) => !isArchivedView || def.column !== ServiceOrdersTableColumns.ACTION_BUTTON,
    );
